import { useWatch, useFormContext } from "react-hook-form";
import { AvailableVolumeTemplates, FontStyles } from "../../constants";
import { OfferInterface } from "../../Types";
import { FONTS } from "../../../../utilities/fonts";
import FormSubSection from "../../CommonFields/FormSubSection";
import {
    FormCheckbox,
    FormColorPicker,
    FormFontsSelect,
    FormInputWithUnit,
    FormRangeSlider,
    FormSelect,
} from "../../../../components/FormFields";
import VolumeDesignSavingsSummaryCard from "./VolumeDesignSavingsSummaryCard";

const VolumeDesignGeneralCard = () => {
    const { control } = useFormContext();

    const showCountdownTimer = useWatch({ control, name: "show_countdown_timer" });
    const showLineAroundTitle = useWatch({ control, name: "show_line_around_title" });
    const allOffers = useWatch({ control, name: "volume_discounts" }) ?? [];
    const showDiscountLabelSize = allOffers?.some(
        (offer: OfferInterface) => !["none", "custom"].includes(offer?.discount_label_type),
    );

    const currTemplate = useWatch({ control, name: "selected_template_volume" });

    return (
        <div>
            <div id="general-design">
                <FormSubSection rowLabel={"General"}>
                    <FormColorPicker name="bundles_design.bundle_widget_bg" label="Bundle Widget Background" />
                    <FormFontsSelect
                        name="bundles_design.bundle_widget_font_family"
                        label="Font Family"
                        options={FONTS}
                        placeholder="Font style"
                    />
                    <FormRangeSlider
                        name="bundles_design.bundle_border_radius"
                        min={0}
                        max={60}
                        label="Corner Radius"
                        helpText="Controls the outer corner radius of the whole bundle widget."
                        suffixTemplate={(val) => `${val}px`}
                    />
                </FormSubSection>
            </div>

            <div id="bundle-title-design">
                <FormSubSection rowLabel={"Bundle Title"}>
                    <FormColorPicker name="bundles_design.bundle_title_color" label="Text Color" />
                    <FormInputWithUnit
                        name="bundles_design.bundle_title_size"
                        min={0}
                        label="Size"
                        placeholder="font size"
                        unitSuffix="px"
                    />
                    <FormSelect
                        name="bundles_design.bundle_title_style"
                        label="Style"
                        options={FontStyles}
                        placeholder="Font style"
                    />
                    {showLineAroundTitle && (
                        <FormColorPicker
                            name="bundles_design.show_line_around_title_color"
                            label="Lines Around Title"
                        />
                    )}
                </FormSubSection>
            </div>

            <div id="special-text-design">
                <FormSubSection rowLabel={"Special Text"}>
                    <FormColorPicker
                        name="bundles_design.special_text_background_color"
                        label="Background Color"
                        disabled={currTemplate == AvailableVolumeTemplates.VERTICAL_ELEGANT}
                    />
                    <FormColorPicker name="bundles_design.special_text_color" label="Text Color" />
                    <FormInputWithUnit
                        name="bundles_design.special_text_font_size"
                        min={0}
                        label="Size"
                        placeholder="font size"
                        unitSuffix="px"
                    />
                    <FormSelect
                        name="bundles_design.special_text_style"
                        label="Style"
                        options={FontStyles}
                        placeholder="Font style"
                    />
                    <div style={{ marginTop: "auto" }}>
                        <FormCheckbox
                            name="bundles_design.special_text_bg_opacity"
                            label="Make background transparent"
                            disabled={currTemplate == AvailableVolumeTemplates.VERTICAL_ELEGANT}
                        />
                    </div>
                </FormSubSection>
            </div>

            {showCountdownTimer && (
                <div id="countdown-timer-design">
                    <FormSubSection rowLabel={"Timer"}>
                        <FormColorPicker name="bundles_design.timer_background_color" label="Background Color" />
                        <FormColorPicker name="bundles_design.timer_text_color" label="Text Color" />
                        <FormColorPicker name="bundles_design.timer_color" label="Timer Color" />
                        <FormInputWithUnit
                            name="bundles_design.timer_font_size"
                            min={0}
                            label="Size"
                            placeholder="font size"
                            unitSuffix="px"
                        />
                        <FormSelect
                            name="bundles_design.timer_font_style"
                            label="Style"
                            options={FontStyles}
                            placeholder="Font style"
                        />
                    </FormSubSection>
                </div>
            )}

            <div id="discount-label-design">
                <FormSubSection rowLabel={"Discount Label"}>
                    <FormColorPicker name="bundles_design.discount_label_background_color" label="Background Color" />
                    <FormColorPicker name="bundles_design.discount_label_color" label="Text Color" />
                    <FormInputWithUnit
                        name="bundles_design.discount_label_font_size"
                        min={0}
                        label="Font Size"
                        placeholder="font size"
                        unitSuffix="px"
                    />
                    <FormSelect
                        name="bundles_design.discount_label_style"
                        label="Style"
                        options={FontStyles}
                        placeholder="Font style"
                    />
                    {showDiscountLabelSize && (
                        <>
                            <FormInputWithUnit
                                name="bundles_design.discount_label_size"
                                min={0}
                                label="Badge Size"
                                placeholder="size"
                                unitSuffix="px"
                            />
                            <FormColorPicker
                                name="bundles_design.discount_label_primary_color"
                                label="Badge Primary Color"
                            />
                            <FormColorPicker
                                name="bundles_design.discount_label_secondary_color"
                                label="Badge Secondary Color"
                            />
                        </>
                    )}
                </FormSubSection>
            </div>

            <VolumeDesignSavingsSummaryCard />
        </div>
    );
};

export default VolumeDesignGeneralCard;
